import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { doGetPromise } from '../../config/core.httpclient-utils';
import { SiteHelpIndex, SiteHelpPage, SiteHelpSearchResponse } from './site-help.types';

const BASE_URL = '/api/site-help';

/**
 * Client for the Flow-served site manual (#47397). The index and pages are
 * anonymous; search needs a logged-in session.
 */
@Injectable()
export class SiteHelpService {
    /** The page shown when no page is asked for, and where leaving the search view returns to by default. */
    static readonly DEFAULT_PAGE = 'End-User-Guide';

    /** The page last read, so leaving the search view can return to it. */
    lastPage: string | null = null;

    private indexPromise: Promise<SiteHelpIndex> | null = null;

    constructor(private http: HttpClient) { }

    /** The table of contents. The manual only changes with a new image, so it is fetched once per page load. */
    getIndex(): Promise<SiteHelpIndex> {
        if (!this.indexPromise) {
            this.indexPromise = doGetPromise(this.http, `${BASE_URL}/index`);
            // A failed load must be retried on the next visit rather than cached.
            this.indexPromise.catch(() => { this.indexPromise = null; });
        }
        return this.indexPromise;
    }

    getPage(slug: string): Promise<SiteHelpPage> {
        return doGetPromise(this.http, `${BASE_URL}/pages/${encodeURIComponent(slug)}`);
    }

    search(q: string, pageNum: number = 1, pageSize: number = 10): Promise<SiteHelpSearchResponse> {
        return doGetPromise(this.http, `${BASE_URL}/search`, { q, page_num: pageNum, page_size: pageSize });
    }

    /**
     * Maps a search hit's `source_url` (`/site-help/<slug>`) to the page slug,
     * or null if it is not a manual page.
     */
    static slugFromUrl(url: string): string | null {
        const m = /^\/site-help\/([^/?#]+)/.exec(url || '');
        return m ? decodeURIComponent(m[1]) : null;
    }
}
